import { useTranslation } from "react-i18next";
import { validateEmail50 } from "../Form/Validations";
import { resolveTipoReportes } from "./utils";
import { Form, withFormik } from "formik";
import { CustomError, FormRow, MultiSelectRow, SelectRow, TituloParametros, capitalize } from "../Form/Form";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FarmContext } from "../../contexts/FarmContext";
import { validateForm } from "./validations";
import { RadioButton } from "primereact/radiobutton";
import DropdownPrime from "../PrimeComponents/DropdownPrime";
import CalendarPrime from "../PrimeComponents/CalendarPrime";
import { createDateTimeFromString, getTimeFromDate, hourSet } from "../../utils/DataUtils";

const ReportForm = ({ handleSubmit, data, values, setFieldValue, errors,
    touched, sectorsInfo, fields, setFieldTouched, setValues, bulkInfo, dataFarm, ...moreProps
}) => {
    const { t, i18n } = useTranslation();
    const [tipoChanged, setTipoChanged] = useState(false);

    const daysTranslated = t("forms.graphDays").split("-"); // starting from 0: Domingo
    const [_, dispatch] = useContext(FarmContext);
    const pumpsys = dispatch({ type: "get", path: "pumpsys" });
    const usersData = dispatch({ type: "get", path: "users" });
    const [emailsOptions, setEmailsOptions] = useState(usersData?.data?.users?.map(u => ({ key: u.email, label: u.email }) ));
    const [pumpsysState, setPumpSysState] = useState(null);
    const [farmHasFerti, setFarmHasFerti] = useState(null);
    const multiSectores = useRef(null);
    const multiEquipos = useRef(null);
    const multiCampos = useRef(null);

    useEffect(() => {
        if (Array.isArray(pumpsys)){
            // sort pumsys and setState
            pumpsys.sort((a, b) => (a.equipo_orden < b.equipo_orden) ? -1 : 1);
            setPumpSysState(pumpsys);
        }
    }, [pumpsys])

    useEffect(() => {
        if (tipoChanged){
            // when tipo_reporte changes, we need to reset multiselect values, because options
            // could change according each selection
            setValues({ ...values, sectores: [], equipos: [], campos: [] });
            // this will fix a bug  with multi-select when clearing selected options (the text)
            // its just a visual bug beacuse values are updating correctly
            const els = document.getElementsByClassName("e-multi-select-wrapper");
            Array.from(els).forEach(wrapper => {
                const spans = wrapper.getElementsByClassName("e-delim-values");
                Array.from(spans).forEach(span => {
                    span.textContent = "";
                })
            })
            setTipoChanged(false);
        }
    }, [setValues, tipoChanged, values]);
    
    const tipoReportesOptions = useMemo(() => {
        const farmHasWeatherZones = sectorsInfo?.some(sec => sec.tipo?.includes(1));
        const showDeseretReport = dataFarm?.deseret_report_mon_data;
        let reportes = resolveTipoReportes(t);
        
        reportes = reportes.filter(r => {
            if (!farmHasWeatherZones && (r.key === "CLIMA" || r.key === "BALANCE_HIDRICO")) return false;
            if (!showDeseretReport && r.key === "DESERET") return false;
            if (farmHasFerti === false && r.key.startsWith("FERTIRRIEGO")) return false;
            return true;
        })

        return reportes;
    }, [dataFarm?.deseret_report_mon_data, farmHasFerti, sectorsInfo, t]);
    
    const REPORTES_WITH_SECTOR = tipoReportesOptions?.filter(tipo => tipo.parameters.includes("SECTORES")).map(tipo => tipo.key);
    const REPORTES_WITH_EQUIPO = tipoReportesOptions?.filter(tipo => tipo.parameters.includes("EQUIPOS")).map(tipo => tipo.key);
    const REPORTES_WITH_CAMPO = tipoReportesOptions?.filter(tipo => tipo.parameters.includes("CAMPOS")).map(tipo => tipo.key);
    
    const numberDaysOptions = (() => {
        const a = Array(31);
        let b = 0;
        while (b < 31) a[b++] = { key: b, label: `0${b}`.slice(-2) };
        return a;
    })();

    const labelDaysOptions = [
        { key: 1, label: daysTranslated[1]  },
        { key: 2, label: daysTranslated[2] },
        { key: 3, label: daysTranslated[3] },
        { key: 4, label: daysTranslated[4] },
        { key: 5, label: daysTranslated[5] },
        { key: 6, label: daysTranslated[6] },
        { key: 7, label: daysTranslated[0] },
    ];

    const periodoReporteOptions = useMemo(() => {
        const selectedReporte = tipoReportesOptions.find(t => t.key === values?.tipo_reporte);
        if (!selectedReporte) return [];
        const allPeriods = [
            { key: "1 week window", label: "1 "+ t("automaticReports.week")  }, 
            { key: "2 week window", label: "2 " + t("automaticReports.weeks") },
            { key: "1 month window", label: "1 " + t("automaticReports.month") },
            { key: "3 month window", label: "3 " + t("automaticReports.months") },
            { key: "6 month window", label: "6 " + t("automaticReports.months") },
            { key: "1 year window", label: "1 " + t("automaticReports.year") },
            { key: "2 year window", label: "2 " + t("automaticReports.years") },
            { key: "3 year window", label: "3 " + t("automaticReports.years") },
            { key: "1 day", label: t("automaticReports.today")   }, // from 00:00 to the date hour
            { key: "1 week", label: t("automaticReports.thisWeek")  }, // from <mondayDate>/<month> to the date
            { key: "1 month", label: t("automaticReports.thisMonth")  }, // from 01/<month> to the date
            { key: "1 year", label: t("automaticReports.thisYear")  }, // from 01/01 to the date
        ];
        return selectedReporte.validPeriods?.map(period => allPeriods.find(p => p.key === period));
    }, [t, tipoReportesOptions, values?.tipo_reporte]) 

    const frecuenciaEnvioOptions = [
        { key: 'daily', label: t("automaticReports.daily") },
        { key: 'weekly', label: t("automaticReports.weekly") },
        { key: 'monthly', label: t("automaticReports.monthly") },
        { key: 'annually', label: t("automaticReports.yearly") },
    ];

    const occurrenceWeekOptions = [
        { key: "1", label: t("automaticReports.sendFirst") },
        { key: "2", label: t("automaticReports.sendSecond") },
        { key: "3", label: t("automaticReports.sendThird") },
        { key: "4", label: t("automaticReports.sendFourth") },
        { key: "L", label: t("automaticReports.sendLast") }
    ];

    const equiposOptions = useMemo(() => {
        if (!pumpsysState?.length) return []
        let equiposWithFert = pumpsysState.filter(equipo => equipo.hidraulicas?.some(hidr => hidr?.tipo === 6));
        if (equiposWithFert.length > 0) setFarmHasFerti(true);
        else setFarmHasFerti(false);

        let optionsResult = values?.tipo_reporte?.startsWith("FERTIRRIEGO") ? equiposWithFert : pumpsysState;
        
        return optionsResult?.map(({ equipo_id, nombre }) => ({ key: equipo_id, label: nombre })) ?? [];
    }, [pumpsysState, values?.tipo_reporte]);

    const zonesSelectorOptions = useMemo(() => {
        if (!values?.tipo_reporte) return [];

        const selectedReporte = tipoReportesOptions.find(t => t.key === values?.tipo_reporte);
        if (!selectedReporte) return [];

        const zoneType = selectedReporte.tipoSector;
        let zones, showGanttName;
        switch (zoneType) {
            case "CLIMA":
                zones = sectorsInfo?.filter(sec => sec.tipo?.includes(1)) || [];
                break;
            case "RIEGO":
                zones = sectorsInfo?.filter(sec => sec.tipo?.includes(3)) || [];
                // irrigation sector must display gantt name
                if (selectedReporte.parameters.includes("SECTORES")){
                    showGanttName = true
                }
                break;
            case "CLIMA-RIEGO":
                zones = sectorsInfo?.filter(sec => sec.tipo?.includes(1) || sec.tipo?.includes(3)) || [];
                break;
            default:
                break;
        }

        
        const result = [];
        // pumpsysState is an array of sorted pumpsys according 'orden' prop.
        if (Array.isArray(pumpsysState)){
            for (const equipo of pumpsysState){
                if (values?.equipos?.length > 0 && !values?.equipos?.includes(equipo.equipo_id)) continue;
                if (values?.tipo_reporte?.startsWith("FERTIRRIEGO") && !equipo.hidraulicas?.some(hidr => hidr?.tipo === 6)) continue;
                const sectoresEquipo = zones.filter(s => s.equipo[0] === equipo.equipo_id);
                sectoresEquipo.sort((a, b) => {
                    if (a.orden !== b.orden) {
                      return a.orden - b.orden; 
                    } else if (a.nombre_alternativo !== b.nombre_alternativo) {
                      if (!a.nombre_alternativo) return 1; 
                      if (!b.nombre_alternativo) return -1; 
                      return a.nombre_alternativo.localeCompare(b.nombre_alternativo); 
                    } else {
                      return a.nombre.localeCompare(b.nombre); 
                    }
                });
                for (const sector of sectoresEquipo){
                    result.push({
                        key: sector.sector_id,
                        label: showGanttName ? (sector.nombre_alternativo || sector.nombre) : sector.nombre,
                        equipo: equipo.nombre
                    })
                }
            }
        } 

        if (!values?.equipos?.length){
            const zonesWithNoPumpsys = zones.filter(s => !s.equipo[0]);
            for (const zone of zonesWithNoPumpsys){
                result.unshift({
                    key: zone.sector_id,
                    label: showGanttName ? (zone.nombre_alternativo || zone.nombre) : zone.nombre,
                    equipo: ""
                })
            }
        }

        return result;
    }, [pumpsysState, sectorsInfo, tipoReportesOptions, values?.tipo_reporte, values?.equipos]);

    const camposOptions = useMemo(() => {
        return fields?.map(field => {
            return {
                key: parseInt(field.id.split("-")[1], 10),
                label: field.name
            }
        })
    }, [fields]);
    const onEmailSelect = (props) => {
        const users = props.value.map(u => u.key);
        if(users?.length < values?.mails_envio_reporte?.length){
            setFieldValue('mails_envio_reporte', users);
        }else{
            const newEmail = users?.[users?.length-1];
            let error = validateEmail50(newEmail);
            setFieldTouched('mails_envio_reporte', true);
            if (!error && newEmail) {
                const emailLower = newEmail.toLowerCase();
                const emailAlreadyInserted = values.mails_envio_reporte?.find(e => e.toLowerCase() === emailLower);
    
                if (!emailAlreadyInserted) {
                    // dont allow more than 20 emails
                    if (values.mails_envio_reporte?.length + 1 > 20){
                        return;
                    }
                    setFieldValue('mails_envio_reporte', [...(values?.mails_envio_reporte ?? []), emailLower]);
                    if (!emailsOptions.some(e => e.key === emailLower)){
                        setEmailsOptions((prevState) => ([...prevState, { key: emailLower, label: emailLower }]));
                    }
                } 
            }
        }
    }

    const hasEquipoWarningMsg = !(values?.tipo_reporte === "BALANCE_HIDRICO") && !(values?.tipo_reporte === "GANTT");
    useEffect(() => {
        moreProps?.innerRef?.current?.validateForm();
    }, [values.tipo_reporte, values.periodo_reporte, values.freq, values.day_of_week])
    return (
        <div id="formReports">
            <Form id={"reports_form"} onSubmit={handleSubmit}>
                <div className='row'>
                    <div className='col-12 text-center'>
                        <div tabIndex="0" />
                        <div tabIndex="1">
                            <FormRow
                                edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "nombre" ? false : true}
                                dato1={data?.nombre}
                                nombre1={t("general.name")}
                                campo1={"nombre"}
                                error1={touched.nombre && errors.nombre && t(errors.nombre)}
                            />
                        </div>
                        <SelectRow
                            edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "tipo_reporte" ? false : true}
                            campo={"tipo_reporte"}
                            nombre={t("general.type")}
                            options={tipoReportesOptions}
                            optionKey={"key"}
                            optionLabel={"label"}
                            placeholder={t("general.type")}
                            error={touched.tipo_reporte && errors.tipo_reporte && t(errors.tipo_reporte)}
                            dataValue={values?.tipo_reporte}
                            width={"100%"}
                            extraOnChange={(value) => {
                                setFieldValue('tipo_reporte', value);
                                setFieldTouched('tipo_reporte', true);
                                setTipoChanged(true);
                            }}
                        />
                        <SelectRow
                            edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "periodo_reporte" ? false : true}
                            campo={"periodo_reporte"}
                            nombre={t("automaticReports.timeRange")}
                            options={periodoReporteOptions}
                            optionKey={"key"}
                            optionLabel={"label"}
                            placeholder={t("automaticReports.timeRange")}
                            error={touched.periodo_reporte && errors.periodo_reporte && t(errors.periodo_reporte)}
                            extraOnChange={(value) => {
                                setFieldTouched('periodo_reporte', true);
                            }}
                            dataValue={values?.periodo_reporte}
                            width={"100%"}
                        />
                        {/* Equipos */}
                        { REPORTES_WITH_EQUIPO.includes(values?.tipo_reporte) && equiposOptions?.length > 0 && (
                            <div className="multiReporte">
                                <MultiSelectRow
                                    edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "equipos" ? false : true}
                                    campo={"equipos"}
                                    nombre={hasEquipoWarningMsg ? capitalize(t("farms.etiqueta.equipos")) + " *" : capitalize(t("farms.etiqueta.equipos"))}
                                    options={equiposOptions}
                                    optionKey={"key"}
                                    optionLabel={"label"}
                                    groupBy={"equipo"}
                                    placeholder={capitalize(t("farms.etiqueta.equipos"))}
                                    error={touched.equipos && errors.equipos && t(errors.equipos)}
                                    dataValue={equiposOptions.filter(s => values?.equipos?.includes(s.key))}
                                    selectionLimit={values?.tipo_reporte === "BALANCE_HIDRICO" ? 1 : 1000}
                                    locale={i18n.language}
                                    onChange={(props) => {
                                        const equipos = props?.value?.map(s => s?.key);
                                        setFieldValue('equipos', equipos);
                                    }}
                                />
                                {
                                    hasEquipoWarningMsg &&
                                    <p className="font-italic p-2">
                                        {`* ${t("automaticReports.warningMsg")}`}
                                    </p>
                                }
                            </div>
                        )}
                        {/* Sectores */}
                        { REPORTES_WITH_SECTOR.includes(values?.tipo_reporte) && zonesSelectorOptions?.length > 0 && (
                            <div className="multiReporte">
                                <MultiSelectRow 
                                    edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "sectores" ? false : true}
                                    campo={"sectores"}
                                    nombre={capitalize(t("farms.etiqueta.sectores")) +" *"}
                                    options={zonesSelectorOptions}
                                    optionKey={"key"}
                                    optionLabel={"label"}
                                    groupBy={"equipo"}
                                    placeholder={capitalize(t("farms.etiqueta.sectores"))}
                                    error={touched.sectores && errors.sectores && t(errors.sectores)}
                                    dataValue={zonesSelectorOptions.filter(s => values?.sectores?.includes(s.key))}
                                    enabled={values?.tipo_reporte === "BALANCE_HIDRICO" ? (values?.equipos?.length > 0 ? true : false) : true}
                                    locale={i18n.language}
                                    onChange={(props) => {
                                        const sectors = props?.value?.map(s => s?.key);
                                        setFieldValue('sectores', sectors);
                                    }}
                                />
                                <p className="font-italic p-2">
                                    {`* ${t("automaticReports.warningMsg")}`}
                                </p>
                            </div>
                        )}
                        {/* Campos */}
                        { REPORTES_WITH_CAMPO.includes(values?.tipo_reporte) && camposOptions?.length > 0 && (
                            <div className="multiReporte">
                                <MultiSelectRow
                                    edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "campos" ? false : true}
                                    campo={"campos"}
                                    nombre={capitalize(t("farms.farms"))}
                                    options={camposOptions}
                                    optionKey={"key"}
                                    optionLabel={"label"}
                                    placeholder={capitalize(t("farms.farms"))}
                                    error={touched.campos && errors.campos && t(errors.campos)}
                                    dataValue={camposOptions.filter(s => values?.campos?.includes(s.key))}
                                    onChange={(props) => {
                                        const campos = props?.value?.map(c => c?.key);
                                        setFieldValue('campos', campos);
                                    }}
                                    fRef={multiCampos}
                                    locale={i18n.language}
                                />
                            </div>
                        )}
                        {
                            !bulkInfo?.bulkEnabled && (
                                <TituloParametros key={"envioTitle"} titulo={t("automaticReports.configuracion")} />
                            )
                        }
                        {
                            emailsOptions?.length > 0 && (
                            <div className="multiReporte">
                                <MultiSelectRow
                                    edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "mails_envio_reporte" ? false : true}
                                    campo={"mails_envio_reporte"}
                                    nombre={t("farms.pozo.reportesEmails")}
                                    options={emailsOptions}
                                    optionKey={"key"}
                                    optionLabel={"label"}
                                    placeholder={t("farms.pozo.reportesEmails")}
                                    error={touched.mails_envio_reporte && errors.mails_envio_reporte && t(errors.mails_envio_reporte)}
                                    dataValue={emailsOptions.filter(u => values?.mails_envio_reporte?.includes(u.key))}
                                    selectionLimit={20}
                                    display="chip"
                                    showSelectAll={false}
                                    showClear={false}
                                    onChange={onEmailSelect}
                                    onRemove={(args) => {
                                        const users = args.value?.map(u => u?.key);
                                        setFieldValue('mails_envio_reporte', users);
                                        setFieldTouched('mails_envio_reporte', true);
                                    }}
                                    locale={i18n.language}
                                />
                            </div>
                            )
                        }
                        {
                            // Aditional validation
                            touched.mails_envio_reporte && errors.customEmailError && !errors.mails_envio_reporte ?
                            <CustomError key={"error_mails_envio_reporte"} 
                                i18nLabel={"farms.pozo.reportesEmails"} errorValue={errors.customEmailError} 
                            />
                            : null
                        }
                        <SelectRow
                            edit={bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "freq" ? false : true}
                            campo={"freq"}
                            nombre={t("automaticReports.frequency")}
                            options={frecuenciaEnvioOptions}
                            optionKey={"key"}
                            optionLabel={"label"}
                            placeholder={t("automaticReports.frequency")}
                            error={touched.freq && errors.freq && t(errors.freq)}
                            dataValue={values?.freq}
                            touched={touched.freq}
                            extraOnChange={() => setFieldValue("monthly_check", "last-day")}
                        />
                        {
                            (values.freq === "weekly") && (
                                <SelectRow
                                    edit={true}
                                    campo={"day_of_week"}
                                    nombre={t("automaticReports.sendOnDay")}
                                    options={labelDaysOptions}
                                    optionKey={"key"}
                                    optionLabel={"label"}
                                    placeholder={t("automaticReports.sendOnDay")}
                                    error={touched.day_of_week && errors.day_of_week && t(errors.day_of_week)}
                                />
                            )
                        }
                        {
                            values.freq === "monthly" && (
                            <div className="text-center"> 
                                <div className="row linea-formulario">
                                    <div className="col-6 col-md-6 text-md-left" style={{display:"flex"}}>
                                        <RadioButton
                                            style={{marginTop:".25rem"}}
                                            inputId="monthly_check_last_day"
                                            name="monthly_check"
                                            value="last-day"
                                            onChange={(e) => setFieldValue("monthly_check", e.value)}
                                            checked={values?.monthly_check === "last-day"} />
                                        <label htmlFor="monthly_check_last_day" className="ml-2">{t("automaticReports.lastDayOfMonth")}</label>
                                    </div>
                                </div>
                                <div className="row linea-formulario">
                                    <div className="col-4 col-md-4 text-md-left" style={{display:"flex"}}>
                                        <RadioButton
                                            style={{marginTop:".25rem"}}
                                            inputId="monthly_check_day"
                                            name="monthly_check"
                                            value="day"
                                            onChange={(e) => setFieldValue("monthly_check", e.value)}
                                            checked={values?.monthly_check === "day"} />
                                        <label htmlFor="monthly_check_day" className="ml-2">{t("automaticReports.sendOnDay")}</label>
                                    </div>
                                    <div className="mb-2 col-4 col-md-4 d-block">
                                        <div className="col-md-10 p-0">
                                            <DropdownPrime
                                                disabled={(values.monthly_check !== "day")}
                                                name="day_of_month"
                                                value={values?.day_of_month}
                                                onChange={(e) => {
                                                    setFieldValue("day_of_month",e?.value);
                                                }}
                                                options={numberDaysOptions} optionLabel="label"
                                                optionValue="key"
                                                placeholder={t("automaticReports.day")}
                                            />
                                        
                                        </div>
                                    </div>
                                </div>
                                <div className="row linea-formulario">
                                    <div className="col-4 col-md-4 text-md-left" style={{display:"flex"}}>
                                        <RadioButton
                                            style={{marginTop:".25rem"}}
                                            inputId="monthly_check_day-of-week"
                                            name="monthly_check"
                                            value="day-of-week"
                                            onChange={(e) => setFieldValue("monthly_check", e.value)}
                                            optionValue="key"
                                            checked={values?.monthly_check === "day-of-week"} />
                                        <label htmlFor="monthly_check_day-of-week" className="ml-2">{t("automaticReports.sendThe")}</label>
                                    </div>
                                    <div className="mb-2 col-4 col-md-4 d-block">
                                        <div className="col-md-10 p-0">
                                            <DropdownPrime
                                                disabled={(values.monthly_check !== "day-of-week")}
                                                name="occurrence_in_week"
                                                value={values?.occurrence_in_week}
                                                onChange={(e) => {
                                                    setFieldValue("occurrence_in_week",e?.value);
                                                }}
                                                options={occurrenceWeekOptions} optionLabel="label"
                                                optionValue="key"
                                                placeholder={t("automaticReports.day")}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-2 col-4 col-md-4 d-block">
                                        <div className="col-md-10 p-0">
                                            <DropdownPrime
                                                disabled={(values.monthly_check !== "day-of-week")}
                                                name="day_of_week"
                                                value={values?.day_of_week}
                                                onChange={(e) => {
                                                    setFieldValue("day_of_week",e?.value);
                                                }}
                                                options={labelDaysOptions} optionLabel="label"
                                                optionValue="key"
                                                placeholder={t("automaticReports.day")}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )
                        }
                        {
                            errors.monthly_check || errors.day_of_month || (errors.monthly_check && errors.day_of_week) || errors.occurrence_in_week ?
                            <CustomError key={"errors_monthly_check"} 
                                i18nLabel={t("automaticReports.monthlyConfig")} 
                                errorValue={errors.monthly_check || errors.day_of_month || errors.day_of_week || errors.occurrence_in_week} 
                            />
                            : null
                        }
                        {
                            bulkInfo?.bulkEnabled  && bulkInfo?.fieldToEdit !== "day_of_year" ? false : (values.freq === "annually" && (
                            <div className="row linea-formulario">
                                <div className="col-6 col-md-6 text-md-right">
                                    <label className='boldie'>{t("automaticReports.sendOnDay")}</label>
                                </div>
                                <div className="mb-2 col-12 col-md-6 d-block">
                                    <div className="col-md-10 p-0 d-flex">
                                        <CalendarPrime
                                            t={t}
                                            id='day_of_year' 
                                            minDate={new Date(new Date().getFullYear(), 0, 1, 0, 0, 0)}
                                            maxDate={new Date(new Date().getFullYear(), 11, 31, 23, 59, 59)}
                                            name="day_of_year"
                                            placeholder={t("automaticReports.sendOnDay")}
                                            value={values?.day_of_year}
                                            dateFormat="dd/mm"
                                            locale={i18n.language}
                                            onChange={(e) => {
                                                if (!e.value) return;
                                                if (e.value.getMonth() === 1 && e.value.getDate() === 29){
                                                    e.value.setDate(28);
                                                }
                                                setFieldValue('day_of_year', e.value);
                                                } 
                                            }
                                            showButtonBar
                                        />
                                    </div>
                                </div>
                                {
                                    touched.day_of_year && errors.day_of_year ?
                                    <div className="mt-2 mb-2 col-12 d-block">
                                        <div className="alert alert-warning m-0" role="alert">{t("automaticReports.fromDay")}: {t(errors.day_of_year)}</div>
                                    </div>
                                    : null
                                }
                            </div> 
                            )) 
                            
                        }
                        {
                            bulkInfo?.bulkEnabled && bulkInfo?.fieldToEdit !== "time" ? false : (
                            <div className="row linea-formulario">
                                <div className="col-6 col-md-6 text-md-right">
                                    <label className='boldie'>{t("automaticReports.sendAt")}</label>
                                </div>
                                <div className="mb-2 col-12 col-md-6 d-block">
                                    <div className="col-md-10 p-0 d-flex">
                                            <DropdownPrime
                                                filter={false}
                                                name="time"
                                                value={values?.time}
                                                onChange={(e) => {
                                                    setFieldValue('time', e.value);
                                                }}
                                                options={hourSet}
                                                placeholder={t("automaticReports.sendAt")}
                                            />
                                    </div>
                                </div>
                                {
                                    touched.time && errors.time ?
                                    <div className="mt-2 mb-2 col-12 d-block">
                                        <div className="alert alert-warning m-0" role="alert">{t("automaticReports.sendAt")}: {t(errors.time)}</div>
                                    </div>
                                    : null
                                }
                            </div>
                            )
                        }
                    </div>
                </div>
            </Form>
        </div>
    )
}

const FormikForm = withFormik({
    mapPropsToTouched: () => {
        return {}
    },
    mapPropsToStatus: (props) => {
      return {
        edit: (props && props.edit) || false,
      }
    },
    mapPropsToValues: (props) => {
      const data = props.data || {};
      let date, month;
      if (data.day_of_year){
        const splitted = data.day_of_year.split("/");
        date = parseInt(splitted[0], 10);
        month = parseInt(splitted[1], 10) - 1;
      }
      return {
        nombre: data.nombre ?? "",
        tipo_reporte: data.tipo_reporte ?? null,
        periodo_reporte: data.periodo_reporte ?? null,
        freq: data.freq ?? null,
        monthly_check: data.monthly_check ?? null,
        mails_envio_reporte: data.mails_envio_reporte ?? [],
        time: data.time ?? null,
        day_of_week: data.day_of_week ?? null,
        occurrence_in_week: data.occurrence_in_week ?? null,
        day_of_month: data.day_of_month ?? null,
        sectores: data.sectores ?? null,
        equipos: data.equipos ?? null,
        campos: data.campos ?? null,
        day_of_year: (data.day_of_year && new Date(new Date().getFullYear(), month, date, 0, 0, 0))  ?? null,
      }
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    validate: validateForm,
    handleSubmit: (values, { props, ...actions }) => {
      props.submit(values, props, actions);
      if (!props.cancel) actions.setStatus({ edit: false });
    }
})(ReportForm);


export { FormikForm }